import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { EwentsTooltipDirective } from '@ewentscompany/ewents-components';
import { Router } from '@angular/router';
import { LoginService } from '../../auth/login.service';
import { AssignmentDownPaymentService } from '../../assignments/assignment-down-payment.service';
import { MENU_STRUCTURE, MenuItem } from '../../shared/constants/api.constants';
import { CUSTOM_SVG_ICONS } from '../../shared/constants/custom-icons.constants';
import { submenuContentHeight, submenuFlyoutBox } from './sidebar-submenu-position';
import { PermissionUtils } from '../../shared/utils/permission.utils';

/** Persists collapsed/expanded across reloads (CRM-only preference). */
const SIDEBAR_COLLAPSED_KEY = 'crm.sidebar.collapsed' as const;

interface SidebarSubItem {
  key: string;
  label: string;
  route: string;
  icon: string;
  isCustomIcon: boolean;
  dividerBefore: boolean;
  matchRoutes?: string[];
}

interface SubmenuPosition {
  top: number | null;
  bottom: number | null;
  left: number;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, EwentsTooltipDirective],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  @Output() menuItemSelected = new EventEmitter<string>();

  /** Preference: icon-only rail when true. Desktop-only; ignored under 1024px. */
  readonly isCollapsed = signal(readSidebarCollapsed());

  openSectionKey: string | null = null;
  submenuPosition: SubmenuPosition | null = null;

  submenuTop = 0;
  submenuLeft = 0;

  private openAnchorEl: HTMLElement | null = null;
  /** Delay close so the pointer can cross the rail → flyout without dropping the menu. */
  private closeSubmenuTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public permissionUtils: PermissionUtils,
    public downPaymentService: AssignmentDownPaymentService,
    private readonly router: Router,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly loginService: LoginService
  ) {}

  /** Collapsed chrome only on desktop; stacked mobile layout always shows labels. */
  get showCollapsedUi(): boolean {
    return this.isCollapsed() && !this.isMobileLayout();
  }

  toggleCollapsed(event?: Event): void {
    event?.stopPropagation();
    if (this.isMobileLayout()) {
      return;
    }
    const next = !this.isCollapsed();
    this.isCollapsed.set(next);
    writeSidebarCollapsed(next);
    this.closeSubmenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeSubmenu();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.repositionOpenSubmenu();
  }

  private isMobileLayout(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
  }

  get currentPath(): string {
    return this.router.url.split('?')[0];
  }

  get visibleSections(): MenuItem[] {
    return MENU_STRUCTURE.filter(section => this.isSectionVisible(section));
  }

  get openSubmenuItems(): SidebarSubItem[] {
    if (!this.openSectionKey) {
      return [];
    }

    const section = this.visibleSections.find(item => item.key === this.openSectionKey);
    return section ? this.getSubmenuItems(section) : [];
  }

  onSectionMouseEnter(section: MenuItem, anchor: HTMLElement): void {
    this.cancelCloseSubmenu();
    if (this.getSubmenuItems(section).length > 0) {
      this.openSubmenu(section.key, anchor);
      return;
    }

    this.closeSubmenu();
  }

  onSidebarMouseLeave(): void {
    this.scheduleCloseSubmenu();
  }

  onFloatingSubmenuMouseEnter(): void {
    this.cancelCloseSubmenu();
  }

  onSidebarNavScroll(): void {
    this.repositionOpenSubmenu();
  }

  onSectionClick(section: MenuItem, anchor: HTMLElement): void {
    const submenuItems = this.getSubmenuItems(section);
    const hasDirectRoute = !!section.route && !section.disabled;

    if (!hasDirectRoute && submenuItems.length === 0) {
      return;
    }

    this.menuItemSelected.emit(section.label);

    // Prefer flyout when children exist — do not navigate to section.route without
    // an explicit leaf permission (section may be visible only via submenu items).
    if (submenuItems.length > 0) {
      if (this.openSectionKey === section.key) {
        this.closeSubmenu();
        return;
      }

      this.openSubmenu(section.key, anchor);
      return;
    }

    if (hasDirectRoute && section.route && this.shouldShowMenuItem(section)) {
      this.router.navigate([section.route]);
    }

    this.closeSubmenu();
  }

  onSubmenuItemClick(item: SidebarSubItem): void {
    this.menuItemSelected.emit(item.label);
    this.router.navigate([item.route]);
    this.closeSubmenu();
  }

  isSectionActive(section: MenuItem): boolean {
    if (section.route && this.currentPath === section.route) {
      return true;
    }

    // Leaf sections (no flyout): keep active on nested routes like /users/:id
    const hasChildren = (section.children || []).length > 0;
    if (section.route && !hasChildren && this.currentPath.startsWith(`${section.route}/`)) {
      return true;
    }

    return this.getSubmenuItems(section).some(item => this.isSubmenuItemActive(item));
  }

  isSubmenuItemActive(item: SidebarSubItem): boolean {
    const routes = [item.route, ...(item.matchRoutes ?? [])];
    return routes.some(
      route => this.currentPath === route || this.currentPath.startsWith(`${route}/`)
    );
  }

  onLogout(): void {
    this.loginService.logout();
  }

  /**
   * Desktop: pin submenu with position:fixed next to the section button so the
   * nav scroller's overflow-y cannot clip it. Mobile CSS resets to static.
   */
  private updateSubmenuPosition(wrapper?: HTMLElement | null): void {
    if (!wrapper || typeof window === 'undefined') {
      this.submenuTop = 0;
      this.submenuLeft = 0;
      return;
    }
    // Match the mobile breakpoint in sidebar.component.scss — fixed coords unused there.
    if (window.matchMedia('(max-width: 1024px)').matches) {
      this.submenuTop = 0;
      this.submenuLeft = 0;
      return;
    }

    const btn = wrapper.querySelector('.section-item') as HTMLElement | null;
    const rect = (btn ?? wrapper).getBoundingClientRect();
    const rail = this.elementRef.nativeElement.querySelector(
      '.sidebar-container'
    ) as HTMLElement | null;
    this.submenuLeft = rail ? rail.getBoundingClientRect().right : rect.right;
    this.submenuTop = rect.top;
  }

  trackBySection(_: number, section: MenuItem): string {
    return section.key;
  }

  trackBySubItem(_: number, item: SidebarSubItem): string {
    return `${item.label}-${item.route}`;
  }

  getSubmenuItems(section: MenuItem): SidebarSubItem[] {
    const visibleItems = (section.children || []).filter(
      item => this.shouldShowMenuItem(item) && !item.disabled && !!item.route && !item.navHidden
    );

    const deduplicated = new Map<string, SidebarSubItem>();

    visibleItems.forEach(item => {
      const key = `${item.label}-${item.route}`;
      if (!deduplicated.has(key)) {
        deduplicated.set(key, {
          key: item.key,
          label: item.label,
          route: item.route!,
          icon: this.mapSubmenuIcon(item),
          isCustomIcon: this.isCustomIcon(item),
          // Skip divider when this is the first visible row (permissions may hide prior groups).
          dividerBefore: !!item.dividerBefore && deduplicated.size > 0,
          matchRoutes: item.matchRoutes,
        });
      }
    });

    return Array.from(deduplicated.values());
  }

  private isSectionVisible(section: MenuItem): boolean {
    if (section.category !== 'section') {
      return false;
    }

    if (section.disabled) {
      return true;
    }

    if (!this.shouldShowMenuItem(section)) {
      return false;
    }

    const hasVisibleSubmenu = this.getSubmenuItems(section).length > 0;

    if (hasVisibleSubmenu) {
      return true;
    }

    const hasChildren = (section.children || []).length > 0;

    if (hasChildren) {
      return !!section.route && !!section.permissions?.length;
    }

    return !!section.route;
  }

  private openSubmenu(sectionKey: string, anchor: HTMLElement): void {
    this.cancelCloseSubmenu();
    this.openSectionKey = sectionKey;
    this.openAnchorEl = anchor;
    this.submenuPosition = this.computeSubmenuPosition(anchor, sectionKey);
  }

  private scheduleCloseSubmenu(): void {
    this.cancelCloseSubmenu();
    this.closeSubmenuTimer = setTimeout(() => this.closeSubmenu(), 180);
  }

  private cancelCloseSubmenu(): void {
    if (this.closeSubmenuTimer != null) {
      clearTimeout(this.closeSubmenuTimer);
      this.closeSubmenuTimer = null;
    }
  }

  private closeSubmenu(): void {
    this.cancelCloseSubmenu();
    this.openSectionKey = null;
    this.openAnchorEl = null;
    this.submenuPosition = null;
  }

  private repositionOpenSubmenu(): void {
    if (!this.openSectionKey || !this.openAnchorEl) {
      return;
    }

    this.submenuPosition = this.computeSubmenuPosition(this.openAnchorEl, this.openSectionKey);
  }

  // ponytail: fixed escape from the nav scroller; upgrade → CDK Overlay
  private computeSubmenuPosition(anchor: HTMLElement, sectionKey: string): SubmenuPosition {
    const rect = anchor.getBoundingClientRect();
    // Flush to the sidebar's outer right edge (not the padded button). An 8px gap
    // outside the rail made mouseleave close the flyout before the pointer arrived.
    const rail = this.elementRef.nativeElement.querySelector(
      '.sidebar-container'
    ) as HTMLElement | null;
    const left = rail ? rail.getBoundingClientRect().right : rect.right;
    const section = this.visibleSections.find(item => item.key === sectionKey);
    const items = section ? this.getSubmenuItems(section) : [];
    const contentHeight = submenuContentHeight(
      items.length,
      items.filter(item => item.dividerBefore).length
    );
    const footer = document.querySelector('app-footer');
    const footerTop = footer?.getBoundingClientRect().top ?? window.innerHeight;
    const box = submenuFlyoutBox({
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
      contentHeight,
      viewportHeight: window.innerHeight,
      footerTop,
    });

    return {
      top: box.top,
      bottom: null,
      left,
    };
  }

  private shouldShowMenuItem(item: MenuItem): boolean {
    if (item.allowAuthenticated) {
      return true;
    }

    if (!item.permissions || item.permissions.length === 0) {
      // Deny-closed for leaf/entity items; section rails rely on visible children.
      return item.category === 'section';
    }

    return item.permissions.some(permission => this.permissionUtils.hasPermission(permission));
  }

  private mapSubmenuIcon(item: MenuItem): string {
    const svg = this.resolveSvgIconName(item);
    return svg != null ? svg : item.icon || 'circle';
  }

  private isCustomIcon(item: MenuItem): boolean {
    return this.resolveSvgIconName(item) != null;
  }

  /** Prefer registered SVG (by icon or key); else Material font ligature. */
  resolveSvgIconName(item: { key: string; icon: string }): string | null {
    if (item.icon && item.icon in CUSTOM_SVG_ICONS) return item.icon;
    if (item.key in CUSTOM_SVG_ICONS) return item.key;
    // MENU key `vendors` → registry `vendor`
    if (item.key === 'vendors' && 'vendor' in CUSTOM_SVG_ICONS) return 'vendor';
    if (item.key === 'bonus-claims' && 'saving-bag-increase' in CUSTOM_SVG_ICONS) {
      return 'saving-bag-increase';
    }
    return null;
  }
}

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // private mode / quota — preference is best-effort
  }
}
